// Phase J.5c — Customer-portal internal approvals.
//
// Lists OPEN customer_amount_overrun tasks assigned to the current portal
// user. Each card shows the offer amount + threshold + Δ over. Approve /
// reject inline; on approve, the deal moves to 'won' on the forwarder
// side automatically (handled by approval_tasks._apply_terminal_effects).
import React, { useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle, ShieldCheck, AlertOctagon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useT } from "../../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: size,
  fontWeight: weight || 700,
  letterSpacing: ls,
});

const useMyApprovals = () =>
  useQuery({
    queryKey: ["portal-approvals"],
    queryFn: () => axios.get(`${API}/approval-tasks`).then((r) => r.data.items || []),
    refetchInterval: 30_000,
  });

const useDecide = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tid, decision, note }) =>
      axios.post(`${API}/approval-tasks/${tid}/${decision}`, { note }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-approvals"] }),
  });
};

export default function PortalApprovals() {
  const { t } = useT();
  const { data, isLoading } = useMyApprovals();
  const decide = useDecide();
  const [notes, setNotes] = useState({});
  const tasks = data || [];

  const submit = async (task, decision) => {
    const note = notes[task.id] || "";
    if (decision === "reject" && !note.trim()) {
      toast.error(t("portal.approvals.note_placeholder"));
      return;
    }
    try {
      await decide.mutateAsync({ tid: task.id, decision, note });
      toast.success(decision === "approve" ? t("portal.approvals.approve") : t("portal.approvals.reject"));
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Decision failed");
    }
  };

  return (
    <div data-testid="portal-approvals-page">
      <header className="mb-5">
        <h1 style={syne(24, 800, "-0.4px")}>
          <ShieldCheck size={20} className="inline mr-2" />
          {t("portal.approvals.title")}
        </h1>
        <div className="mt-1" style={mono(11, "#94a3b8")}>
          {t("portal.approvals.subtitle")}
        </div>
      </header>

      {isLoading && (
        <div className="flex items-center gap-2 p-3" style={mono(11, "#94a3b8")}>
          <Loader2 size={12} className="animate-spin" /> …
        </div>
      )}
      {!isLoading && tasks.length === 0 && (
        <div className="p-6 rounded text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="portal-approvals-empty">
          <ShieldCheck size={28} color="#0a8754" className="inline mb-2" />
          <div style={mono(12, "#cbd5e1")}>{t("portal.approvals.empty")}</div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {tasks.map((task) => (
          <div
            key={task.id}
            className="rounded-lg p-3"
            style={{ background: "linear-gradient(145deg, rgba(230,167,0,0.06), rgba(126,58,168,0.04))", border: "1px solid rgba(230,167,0,0.32)" }}
            data-testid={`portal-approval-card-${task.id}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <div style={{ ...mono(9, "#e6a700", 700, "0.5px"), textTransform: "uppercase" }}>
                  <AlertOctagon size={11} className="inline mr-1" />
                  Customer amount overrun
                </div>
                <Link to={`/portal/r/${task.deal_id}`} style={{ ...syne(15, 700), color: "#9ec3ec", textDecoration: "none" }}>
                  {task.payload?.deal_name || task.deal_id}
                </Link>
                <div style={mono(9, "#94a3b8")}>{task.deal_id}</div>
              </div>
              <div style={mono(9, "#94a3b8", 700, "0.4px")}>{(task.created_at || "").slice(0, 16).replace("T", " ")}</div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-3 p-2 rounded" style={{ background: "rgba(0,0,0,0.20)" }}>
              <div>
                <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Offer amount</div>
                <div style={syne(18, 800)} data-testid={`portal-approval-amount-${task.id}`}>
                  {task.payload?.amount != null ? `${Number(task.payload.amount).toLocaleString()} ${task.payload?.currency || ""}`.trim() : "—"}
                </div>
              </div>
              <div>
                <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Threshold</div>
                <div style={syne(18, 800)} data-testid={`portal-approval-threshold-${task.id}`}>
                  {task.payload?.threshold != null ? Number(task.payload.threshold).toLocaleString() : "—"}
                </div>
              </div>
              <div>
                <div style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>Δ Over</div>
                <div style={{ ...syne(18, 800), color: "#e6a700" }}>
                  {(task.payload?.amount != null && task.payload?.threshold != null)
                    ? `+${(Number(task.payload.amount) - Number(task.payload.threshold)).toLocaleString()}`
                    : "—"}
                </div>
              </div>
            </div>

            {task.payload?.note && (
              <div className="mb-2 p-2 rounded" style={{ background: "rgba(155,89,182,0.06)", border: "1px solid rgba(155,89,182,0.30)" }}>
                <div style={{ ...mono(8, "#c89adb", 700, "0.5px"), textTransform: "uppercase" }}>Note from acceptor</div>
                <div style={mono(11, "#f0f2f8")}>{task.payload.note}</div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder={t("portal.approvals.note_placeholder")}
                value={notes[task.id] || ""}
                onChange={(e) => setNotes((p) => ({ ...p, [task.id]: e.target.value }))}
                className="flex-1 px-2 py-1.5 rounded"
                style={{ background: "rgba(0,0,0,0.30)", border: "1px solid rgba(255,255,255,0.12)", color: "#f0f2f8", ...mono(11) }}
                data-testid={`portal-approval-note-${task.id}`}
              />
              <button
                type="button"
                onClick={() => submit(task, "approve")}
                disabled={decide.isPending}
                className="px-3 py-1.5 rounded inline-flex items-center gap-1"
                style={{ background: "#0a8754", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.5px"), cursor: "pointer", textTransform: "uppercase" }}
                data-testid={`portal-approval-approve-${task.id}`}
              >
                <CheckCircle2 size={12} /> {t("portal.approvals.approve")}
              </button>
              <button
                type="button"
                onClick={() => submit(task, "reject")}
                disabled={decide.isPending}
                className="px-3 py-1.5 rounded inline-flex items-center gap-1"
                style={{ background: "transparent", border: "1px solid #ef4444", color: "#ef4444", ...mono(10, "#ef4444", 700, "0.5px"), cursor: "pointer", textTransform: "uppercase" }}
                data-testid={`portal-approval-reject-${task.id}`}
              >
                <XCircle size={12} /> {t("portal.approvals.reject")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
